import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {useMemo, useState, useRef, useCallback, useEffect} from 'react';
import {GridColDef, GridSortModel} from '@mui/x-data-grid';
import {Box, Typography} from '@mui/material';
import './History.scss';
import {
  DataGridPro,
  GridDataSource,
  GridGetRowsParams,
  GridDataSourceCacheDefault,
} from '@mui/x-data-grid-pro';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import dayjs from 'dayjs';
import {DocDetailHistory} from '../../../../../api/types/document.type';
import {getHistoryListDocdetails} from '../../../../../api/endpoints/documentApi';
import {t} from 'i18next';
import {NoRowsOverlay} from '../metadata/Metadata';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';

const DEFAULT_PAGE_SIZE = 100;
const THROTTLE_DELAY = 2000;
const ROW_HEIGHT = 40;
const ROW_BUFFER_PX = 300;
const HEADER_MIN_WIDTH = 7;
const DEFAULT_SORT_MODEL: GridSortModel = [{field: 'action', sort: 'asc'}];

const History = () => {
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const documentId = activeDocumentObj?.documentId;
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  // Cache pages
  const cacheRef = useRef<Map<number, DocDetailHistory[]>>(new Map());
  const noCache = useMemo(() => new GridDataSourceCacheDefault({ttl: 0}), []);
  const loadingPagesRef = useRef<Set<number>>(new Set());
  const pagePromiseRef = useRef<Map<number, Promise<DocDetailHistory[]>>>(
    new Map()
  );

  const loader = useCallback(
    () => (
      <Box className="history-loader">
        <ArrLoadingSpinner />
      </Box>
    ),
    []
  );

  // Reset cache when document or sort changes
  useEffect(() => {
    cacheRef.current.clear();
    loadingPagesRef.current.clear();
    pagePromiseRef.current.clear();
    setIsLoading(true);
    setFirstLoadDone(false);
  }, [sortModel]);

  useEffect(() => {
    cacheRef.current.clear();
    loadingPagesRef.current.clear();
    pagePromiseRef.current.clear();
    setIsLoading(true);
    setFirstLoadDone(false);
    // Reset scroll to top
    setSortModel(DEFAULT_SORT_MODEL);
  }, [documentId]);

  const fetchPage = useCallback(
    (page: number): Promise<DocDetailHistory[]> => {
      if (cacheRef.current.has(page)) {
        return Promise.resolve(cacheRef.current.get(page)!);
      }
      //Return in-flight request (DEDUPES)
      if (pagePromiseRef.current.has(page)) {
        return pagePromiseRef.current.get(page)!;
      }
      loadingPagesRef.current.add(page);
      const currentSort = sortModel[0] || DEFAULT_SORT_MODEL[0];
      const promise = (async () => {
        try {
          const queryParams = `?PageNumber=${page}&PageSize=${DEFAULT_PAGE_SIZE}&SourceDocumentId=${documentId || 0}&IsDetailView=true&SortColumn=${currentSort.field}&SortDirection=${currentSort.sort}`;
          const result = await getHistoryListDocdetails(queryParams);
          const rows = Array.isArray(result.documentHistories)
            ? result.documentHistories
            : [];
          cacheRef.current.set(page, rows);
          return rows;
        } catch (error) {
          console.error('Error fetching document history:', error);
          cacheRef.current.set(page, []);
          return [];
        } finally {
          pagePromiseRef.current.delete(page);
          if (!firstLoadDone) {
            setIsLoading(false);
            setFirstLoadDone(true);
          }
        }
      })();
      //Store promise BEFORE awaiting
      pagePromiseRef.current.set(page, promise);

      return promise;
    },
    [documentId, sortModel, firstLoadDone]
  );

  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async ({start, end}: GridGetRowsParams) => {
        const startPage = Math.floor(+start / DEFAULT_PAGE_SIZE) + 1;
        const endPage = Math.floor((end - 1) / DEFAULT_PAGE_SIZE) + 1;
        const rows: (DocDetailHistory & {id: string})[] = [];

        for (let page = startPage; page <= endPage; page++) {
          const pageRows = await fetchPage(page);
          //ONLY stop on first page AND first load
          if (!pageRows.length && start === 0) {
            return {rows: []};
          }

          if (!pageRows.length) {
            break;
          }
          const pageStartIndex = (page - 1) * DEFAULT_PAGE_SIZE;
          const sliceStart = Math.max(+start - pageStartIndex, 0);
          const sliceEnd = Math.min(end - pageStartIndex + 1, pageRows.length);

          rows.push(
            ...pageRows
              .slice(sliceStart, sliceEnd)
              .map((row: DocDetailHistory) => ({
                ...row,
                id: `${row.action}_${row.documentName}_${row.user}_${row.timeStamp}`,
              }))
          );
        }

        return {rows};
      },
    }),
    [fetchPage]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'action',
        headerName: t('docView.headers.docDetails.history.action'),
        flex: 1,
        minWidth: HEADER_MIN_WIDTH,
        renderCell: (param) => (
          <CellWrapper
            toolTipContent={param.value}
            dataTestId={`cell-wrapper-tooltip-content-action-${param.value}`}
            cellParams={param}
          >
            <Typography
              className="cell-content typo-text body3_size"
              data-testid="history-action-value"
              aria-label={param.value}
            >
              {param.value}
            </Typography>
          </CellWrapper>
        ),
      },
      {
        field: 'documentName',
        headerName: t('docView.headers.docDetails.history.documentName'),
        flex: 1,
        minWidth: HEADER_MIN_WIDTH,
        renderCell: (param) => (
          <CellWrapper
            toolTipContent={param.value}
            dataTestId={`cell-wrapper-tooltip-content--documentName-${param.value}`}
            cellParams={param}
          >
            <Typography
              className="cell-content typo-text body3_size"
              data-testid="history-doc-name-value"
              aria-label={param.value}
            >
              {param.value}
            </Typography>
          </CellWrapper>
        ),
      },
      {
        field: 'user',
        headerName: t('docView.headers.docDetails.history.user'),
        flex: 1,
        minWidth: HEADER_MIN_WIDTH,
        renderCell: (param) => (
          <CellWrapper
            toolTipContent={param.value}
            dataTestId={`cell-wrapper-tooltip-content-user-${param.value}`}
            cellParams={param}
          >
            <Typography
              className="cell-content typo-text body3_size"
              data-testid="history-user-value"
              aria-label={param.value}
            >
              {param.value}
            </Typography>
          </CellWrapper>
        ),
      },
      {
        field: 'timeStamp',
        headerName: t('docView.headers.docDetails.history.timestamp'),
        headerClassName: 'last-column',
        flex: 1,
        minWidth: HEADER_MIN_WIDTH,
        renderCell: (param) => {
          const formatted = dayjs(param.value).format('YYYY-MM-DD HH:mm:ss');
          return (
            <CellWrapper
              toolTipContent={param.value}
              dataTestId={`cell-wrapper-tooltip-content-timestamp-${param.value}`}
              cellParams={param}
            >
              <Typography
                className="cell-content typo-text body3_size"
                data-testid="history-timestamp-value"
                aria-label={`Timestamp: ${formatted}`}
              >
                {formatted}
              </Typography>
            </CellWrapper>
          );
        },
      },
    ],
    []
  );

  return (
    <Box className="meta-data-container" data-testid="history-grid-wrapper">
      <DataGridPro
        key={`${documentId}-${sortModel[0]?.field}-${sortModel[0]?.sort}`}
        aria-label="Document history table"
        rowHeight={ROW_HEIGHT}
        rowBufferPx={ROW_BUFFER_PX}
        data-testid="history-tab-grid"
        loading={isLoading}
        disableColumnReorder
        disableColumnResize
        disableColumnFilter
        disableRowSelectionOnClick
        disableColumnMenu
        getRowId={(row) => row.id}
        columns={columns}
        dataSource={dataSource}
        dataSourceCache={noCache}
        onDataSourceError={(error) =>
          console.error('History datasource error:', error)
        }
        lazyLoading
        lazyLoadingRequestThrottleMs={THROTTLE_DELAY}
        sortingOrder={['asc', 'desc']}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        slots={{
          loadingOverlay: loader,
          noRowsOverlay: firstLoadDone ? NoRowsOverlay : () => null,
        }}
        hideFooter
      />
    </Box>
  );
};

export default History;
